import { useEnv } from '@sparklayer-io/spark-dash-core/context/env-context';
import { ApiResponseError, isApiErrorStatus } from '@sparklayer-io/spark-dash-core/types/errors';
import {
  useHandlers,
  withPageProviders,
} from '@sparklayer-io/spark-dash-pages/context/page-context';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Card, Page, Skeleton, Text } from '@ui/themed';
import { useEffect, useState } from 'react';
import { ActionForm } from '../components/action-form';
import { useStaffApi } from '../hooks/use-staff-api';
import type { StaffActionCategory } from '../types';

type ViewActionPageConfig = {
  category: StaffActionCategory;
  titlePrefix: string;
  listRoutePath: string;
  submitLabel?: string;
};

function ViewActionPageInner({
  category,
  titlePrefix,
  listRoutePath,
  submitLabel,
}: ViewActionPageConfig) {
  const env = useEnv();
  const { navigationHandler, notificationHandler } = useHandlers();
  const { staffApi } = useStaffApi();
  const queryClient = useQueryClient();

  const actionType = navigationHandler.params().actionType ?? '';

  const [saving, setSaving] = useState<boolean>(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [formErrors, setFormErrors] = useState<Record<string, string[]>>({});

  const actionQuery = useQuery({
    queryKey: ['staff-action', env.team.id, category, actionType],
    queryFn: () => staffApi.action(env.team.id, category, actionType),
  });

  useEffect(() => {
    const err = actionQuery.error;
    if (!err) return;

    if (isApiErrorStatus(err, 403)) {
      notificationHandler.notice('This action is no longer available for this team');
      navigationHandler.navigate(listRoutePath, { excludeSiteEnv: true });
      return;
    }

    notificationHandler.error('There was a problem loading this action');
    console.error(err);
  }, [actionQuery.error, notificationHandler, navigationHandler, listRoutePath]);

  if (actionQuery.isLoading) return <Skeleton />;

  if (actionQuery.isError || !actionQuery.data) {
    return (
      <Alert variant="error" headingLevel="h3">
        There was a problem loading this action
      </Alert>
    );
  }

  const { action, form, prompts, details } = actionQuery.data;

  const handleSave = async (payload: Record<string, unknown>) => {
    setErrors([]);
    setFormErrors({});
    setSaving(true);

    try {
      await staffApi.performAction(env.team.id, category, actionType, payload);

      notificationHandler.success(`The ${action.name} action completed successfully.`);

      // Reload env to ensure app shows latest state
      queryClient.invalidateQueries({ queryKey: ['get-env'] });
      // Refetch to get latest values from server
      await actionQuery.refetch();
    } catch (err) {
      const data = err instanceof ApiResponseError ? err.data : undefined;
      const resultErrors = (
        data as { result?: { errors?: string[] | Record<string, string[]> } } | undefined
      )?.result?.errors;

      if (isApiErrorStatus(err, 400)) {
        notificationHandler.error(`${action.name} Action - Invalid request`);
        if (resultErrors) {
          setFormErrors(resultErrors as Record<string, string[]>);
        }
      } else if (isApiErrorStatus(err, 422)) {
        notificationHandler.error(`${action.name} Action - Unable to complete action`);
        if (resultErrors) {
          setErrors(resultErrors as string[]);
        }
      } else {
        console.error(err);
        notificationHandler.error(
          `${action.name} Action - There was an issue completing this action.`,
        );
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Page title={`${titlePrefix} — ${action.name}`} description={action.description}>
      <Card>
        {prompts.map((prompt) => (
          <Alert key={prompt} variant="warning" headingLevel="h3">
            {prompt}
          </Alert>
        ))}
        {details.length > 0 && (
          <div className="rounded bg-spark-surface-muted p-2">
            {details.map((detail) => (
              <div key={detail.label} className="flex gap-4 text-sm">
                <Text bold>{detail.label}</Text>
                <Text variant="secondary">{detail.text}</Text>
              </div>
            ))}
          </div>
        )}
        <ActionForm
          schema={form.schema}
          data={form.data}
          errors={formErrors}
          saving={saving}
          submitLabel={submitLabel}
          onSave={handleSave}
        />
        {errors.map((error) => (
          <Alert key={error} variant="error" headingLevel="h3">
            <span className="text-sm text-spark-text-danger">{error}</span>
          </Alert>
        ))}
      </Card>
    </Page>
  );
}

export const ManageBillingActionPage = withPageProviders(() => (
  <ViewActionPageInner
    category="manage-billing"
    titlePrefix="Manage Billing"
    listRoutePath="/staff/manage-billing"
  />
));

export const ManageTeamActionPage = withPageProviders(() => (
  <ViewActionPageInner
    category="manage-team"
    titlePrefix="Manage Team"
    listRoutePath="/staff/manage-team"
    submitLabel="Confirm"
  />
));
